import { useMemo, useState } from 'react';
import { previewBulkImport } from '../../apps/cidr-planner/bulkImport';
import type { IpCountMode } from '../../apps/cidr-planner/types';
import type { DisplayLang } from '../../../shared/utils/pageDisplay';
import { CidrSheet } from './CidrSheet';

type ImportCidrDialogProps = {
  parentCidr: string;
  initialText: string;
  ipCountMode: IpCountMode;
  lang: DisplayLang;
  onClose: () => void;
  onConfirm: (result: {
    text: string;
    payloads: Array<{ cidr: string; name: string }>;
  }) => void;
};

const TEXT = {
  ja: {
    title: 'CIDR テキスト',
    input: 'CIDR 一覧',
    placeholder: '10.0.1.0/24, app\n10.0.2.0/24',
    commentNote: '# でコメントを書けます。',
    errors: 'エラー',
    lineError: (line: number, message: string) => `${line} 行目: ${message}`,
    outOfParent: '親 CIDR の範囲外',
    overlap: '別行と重複',
    duplicate: '入力内で重複',
    invalid: 'CIDR 形式が正しくありません',
    awsTooSmall: 'AWS VPC では /28 より細かいサブネットは不可',
    cancel: 'キャンセル',
    apply: '反映',
    close: '閉じる',
  },
  en: {
    title: 'CIDR text',
    input: 'CIDR list',
    placeholder: '10.0.1.0/24, app\n10.0.2.0/24',
    commentNote: 'Use # for comment lines.',
    errors: 'Errors',
    lineError: (line: number, message: string) => `Line ${line}: ${message}`,
    outOfParent: 'Outside the parent CIDR range',
    overlap: 'Overlaps another line',
    duplicate: 'Duplicate in input',
    invalid: 'Invalid CIDR format',
    awsTooSmall: 'AWS VPC subnets cannot be smaller than /28',
    cancel: 'Cancel',
    apply: 'Apply',
    close: 'Close',
  },
} as const;

function mapError(
  error: string,
  t: {
    outOfParent: string;
    overlap: string;
    duplicate: string;
    invalid: string;
    awsTooSmall: string;
  },
): string {
  if (error === 'OUT_OF_PARENT') return t.outOfParent;
  if (error === 'OVERLAP') return t.overlap;
  if (error === 'DUPLICATE') return t.duplicate;
  if (error === 'AWS_TOO_SMALL') return t.awsTooSmall;
  return t.invalid;
}

export function ImportCidrDialog({
  parentCidr,
  initialText,
  ipCountMode,
  lang,
  onClose,
  onConfirm,
}: ImportCidrDialogProps) {
  const t = TEXT[lang];
  const [text, setText] = useState(initialText);

  const preview = useMemo(
    () => previewBulkImport(text, parentCidr, [], ipCountMode),
    [text, parentCidr, ipCountMode],
  );

  return (
    <CidrSheet title={t.title} closeLabel={t.close} onClose={onClose}>
      <label className="cidr-sheet__field">
        <span className="cidr-sheet__label">{t.input}</span>
        <textarea
          className="cidr-sheet__textarea"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={t.placeholder}
          spellCheck={false}
          rows={10}
        />
      </label>
      <p className="cidr-sheet__hint">{t.commentNote}</p>
      {preview.invalid.length > 0 && (
        <div className="cidr-sheet__field">
          <span className="cidr-sheet__label">{t.errors}</span>
          <ul className="cidr-sheet__preview-list cidr-sheet__preview-list--errors">
            {preview.invalid.map((item) => (
              <li key={`${item.line}-${item.raw}`}>
                {t.lineError(item.line, mapError(item.error, t))}: {item.raw}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="cidr-sheet__actions">
        <button type="button" className="cidr-sheet__btn" onClick={onClose}>
          {t.cancel}
        </button>
        <button
          type="button"
          className="cidr-sheet__btn cidr-sheet__btn--primary"
          disabled={preview.invalid.length > 0}
          onClick={() => {
            onConfirm({
              text,
              payloads: preview.valid.map((item) => ({
                cidr: item.normalized,
                name: item.name,
              })),
            });
          }}
        >
          {t.apply}
        </button>
      </div>
    </CidrSheet>
  );
}
